import * as React from 'react';
import { observer } from 'mobx-react';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { ITeamDecisionDispatcher } from 'typings/teamDecision.dispatcher';
import AutoComplete from 'components/Common/autoComplete';
import { ReportsView, WorkflowStatusListType, AmountCode } from 'typings/enums';
import { defaultSearchResultLimit, emptyFunction } from 'common/constants';
import { TeamDecisionStore } from 'nomst/Stores/Decision/teamDecision.store';
import { NavigationStore } from 'nomst/Stores/Navigation/navigation.store';
import { SendForReviewButton } from 'nomst/Components/Decision/sendForReviewButton';
import { ISearchedEmployee } from 'typings/employee';
import { Toggle } from '@payscale/design';
import './teamDecisionHeader.scss';
import { EmployeeSort } from 'nomst/Stores/Decision/Constants/employeeSort';
import { IEmployeeViewNavigationParameters } from 'typings/navigation';
import { ManagerWorkflowStore } from 'nomst/Stores/Decision/managerWorkflow.store';
import { User } from 'nomst/Stores/User/user';

export interface ITeamDecisionHeaderProps {
    configuration: Configuration;
    teamDecisionDispatcher: ITeamDecisionDispatcher;
    teamDecisionStore: TeamDecisionStore;
    selectedIsoCurrencyCode: string;
    selectedEmployeeSort: EmployeeSort;
    showPromotionsOnly: boolean;
    currentManagerIds: string[];
    directReportsCount: number;
    allReportsCount: number;
    navigationStore: NavigationStore;
    workflowStatus: Nullable<WorkflowStatusListType>;
    budgetCycleId: Nullable<number>;
    shouldHideChangeHistory: Nullable<boolean>;
    managerWorkFlowStore: ManagerWorkflowStore;
    currentUser: User;
}

export class TeamDecisionHeader extends React.Component<ITeamDecisionHeaderProps> {
    private header: Nullable<HTMLDivElement>;
    private approveButtonContainer: Nullable<HTMLDivElement>;
    private headerInitialTop: number;

    constructor(props: ITeamDecisionHeaderProps) {
        super(props);

        this.setHeaderClassBasedOnScroll = this.setHeaderClassBasedOnScroll.bind(this);
        this.handleChangeCurrency = this.handleChangeCurrency.bind(this);
        this.searchEmployees = this.searchEmployees.bind(this);
        this.handleSetFoundEmployee = this.handleSetFoundEmployee.bind(this);
        this.clearFoundEmployee = this.clearFoundEmployee.bind(this);
        this.handleChangeEmployeeSort = this.handleChangeEmployeeSort.bind(this);
        this.handleChangePromotionToggle = this.handleChangePromotionToggle.bind(this);
        this.handleExportReportsToCsv = this.handleExportReportsToCsv.bind(this);
        this.handleChangeView = this.handleChangeView.bind(this);
        this.handleDirectsClick = this.handleDirectsClick.bind(this);
        this.handleAllEmployeesClick = this.handleAllEmployeesClick.bind(this);
        this.handleChangeHistoryViewClick = this.handleChangeHistoryViewClick.bind(this);
        this.searchAllJobTitles = this.searchAllJobTitles.bind(this);
        this.handleSetFilterByJobTitle = this.handleSetFilterByJobTitle.bind(this);
        this.clearFoundEmployee = this.clearFoundEmployee.bind(this);
        this.handleGetFilter = this.handleGetFilter.bind(this);
        this.clearSearchedJobTitle = this.clearSearchedJobTitle.bind(this);
        this.searchAllDepartments = this.searchAllDepartments.bind(this);
        this.handleSetFilterByDepartment = this.handleSetFilterByDepartment.bind(this);
        this.clearSearchedDepartment =  this.clearSearchedDepartment.bind(this);
    }

    componentDidUpdate(): void {
        this.clearFoundEmployee();
    }

    render(): React.ReactNode {
        const { directReportsCount, allReportsCount, currentManagerIds, shouldHideChangeHistory } = this.props;
        const { teamView } = this.props.teamDecisionStore;
        const directsActiveClass = teamView === ReportsView.DirectsOnly ? ' active' : '';
        const allReportsActiveClass = teamView === ReportsView.AllReports ? ' active' : '';
        return (
            <div>
                <div
                    ref={element => this.initializeStickyHeader(element)}
                    className="team-decision-header"
                >
                    <div className="flex-row">
                        <div className="first-sub-header">
                            <div className="tabs tabs--fat">
                                <button
                                    onClick={this.handleDirectsClick}
                                    className={`tab directs${directsActiveClass} pxl-link`}
                                >
                                    Directs Only ({directReportsCount})
                                </button>
                                <button
                                    onClick={this.handleAllEmployeesClick}
                                    className={`tab all-reports${allReportsActiveClass} pxl-link`}
                                    hidden={allReportsCount === directReportsCount || currentManagerIds.length > 1}
                                >
                                    All Employees ({allReportsCount})
                                </button>
                            </div>
                            <div className="employee-search-container">
                                <div
                                    ref={element => this.intializeApproveButton(element)}
                                    className="approve-button-container approve-button-container--hidden"
                                >
                                    <SendForReviewButton 
                                        managerWorkflowStore={this.props.managerWorkFlowStore}
                                        decisionDispatcher={this.props.teamDecisionStore.decisionDispatcher}
                                        user={this.props.currentUser}
                                    />
                                </div>
                                <AutoComplete
                                    id="employeeSearch"
                                    placeholder={this.setPlaceHolderText()}
                                    search={this.searchEmployees}
                                    resultLimit={defaultSearchResultLimit}
                                    onChangeInput={emptyFunction}
                                    onSuggestionSelected={this.handleSetFoundEmployee}
                                    onClearSearch={this.clearFoundEmployee}
                                    getSelectedValue={this.handleGetSelectedValue}
                                    getRenderedSuggestionText={this.getSearchSuggestionText}
                                    initialValue={''}
                                    disableInput={false}
                                    managerIds={this.props.currentManagerIds}
                                />
                            </div>
                        </div>
                        <div className="second-sub-header">
                            <div className="second-sub-header__content">
                                {this.renderCurrencySelection()}
                                {this.renderEmployeeSortOptions()}
                                {this.renderEmployeeJobFilter()}
                                {this.renderEmployeeDepartmentFilter()}
                                {this.renderPromotionToggle()}
                                <div className="history-text">
                                    <button
                                        className="history-link pxl-link"
                                        hidden={!!shouldHideChangeHistory}
                                        onClick={this.handleChangeHistoryViewClick}
                                    >
                                        <i className="icon-clock" />
                                        <span>Change History</span>
                                    </button>
                                    <button className="export-link pxl-link" onClick={this.handleExportReportsToCsv}>
                                        <i className="icon-download" /><span>Export</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="sticky-header-placeholder" />
            </div>
        );
    }

    private initializeStickyHeader(header: Nullable<HTMLDivElement>): void {
        if (!header) {
            return;
        }

        if (this.header !== header) {
            this.header = header;
            this.headerInitialTop = header.getBoundingClientRect().top;
            document.removeEventListener('scroll', this.setHeaderClassBasedOnScroll);
            document.addEventListener('scroll', this.setHeaderClassBasedOnScroll);
        }
    }


    private intializeApproveButton(container: Nullable<HTMLDivElement>): void {
        if (!container) {
            return;
        }

        if (this.approveButtonContainer !== container) {
            this.approveButtonContainer = container;
        }
    }

    private setHeaderClassBasedOnScroll(): void {
        if (!this.header || !this.headerInitialTop || this.headerInitialTop === 0) {
            return;
        }

        const documentTop = document.documentElement.scrollTop || document.body.scrollTop;
        if (documentTop >= this.headerInitialTop) {
            this.header.classList.add('sticky-header');
            this.approveButtonContainer?.classList.remove('approve-button-container--hidden');
            return;
        }

        this.header.classList.remove('sticky-header');
        
        this.approveButtonContainer?.classList.add('approve-button-container--hidden');
    }

    private renderEmployeeSortOptions(): React.ReactNode {
        return (
            <div className="second-sub-header__sort">
                <div className="label">Sort By</div>
                {/*eslint-disable-next-line*/}
                <select
                    id="employee-sort"
                    className="second-sub-header__sort-select"
                    onChange={this.handleChangeEmployeeSort}
                    value={this.props.selectedEmployeeSort}
                >
                    {Object.keys(EmployeeSort).map((sortOption: keyof typeof EmployeeSort) =>
                        (
                            <option
                                key={sortOption}
                                value={EmployeeSort[sortOption]}
                            >
                                {EmployeeSort[sortOption]}
                            </option>
                        )
                    )}
                </select>
            </div>
        );
    }

    private renderEmployeeJobFilter(): React.ReactNode {
        return (
            <div className="employee-job-search-container team-decision-header-search">
                <label htmlFor="employeeJobSearch" className="label">Filter By</label>
                <AutoComplete
                    id="employeeJobSearch"
                    placeholder="Job Title"
                    search={this.searchAllJobTitles}
                    resultLimit={defaultSearchResultLimit}
                    onChangeInput={emptyFunction}
                    onSuggestionSelected={this.handleSetFilterByJobTitle}
                    onClearSearch={this.clearSearchedJobTitle}
                    getSelectedValue={this.handleGetFilter}
                    getRenderedSuggestionText={this.handleGetFilter}
                    initialValue={this.props.teamDecisionStore.filterByJob ?? ''}
                    disableInput={false}
                    managerIds={this.props.currentManagerIds}
                />
            </div>
        );
    }

    private renderEmployeeDepartmentFilter(): React.ReactNode {
        return (
            <div className="employee-department-search-container team-decision-header-search">
                <label htmlFor="employeeDepartmentSearch" className="label">Filter By</label>
                <AutoComplete
                    id="employeeDepartmentSearch"
                    placeholder="Department"
                    search={this.searchAllDepartments}
                    resultLimit={defaultSearchResultLimit}
                    onChangeInput={emptyFunction}
                    onSuggestionSelected={this.handleSetFilterByDepartment}
                    onClearSearch={this.clearSearchedDepartment}
                    getSelectedValue={this.handleGetFilter}
                    getRenderedSuggestionText={this.handleGetFilter}
                    initialValue={this.props.teamDecisionStore.filterByDepartment ?? ''}
                    disableInput={false}
                    managerIds={this.props.currentManagerIds}
                />
            </div>
        );
    }

    private renderCurrencySelection(): React.ReactNode {
        const { configuration, selectedIsoCurrencyCode } = this.props;

        if (!configuration.hasMultipleCurrency) {
            return null;
        }

        return (
            <div>
                <div className="label">Currency</div>
                <select
                    id="currency-select"
                    className="second-sub-header__currency-select"
                    onBlur={this.handleChangeCurrency}
                    onChange={this.handleChangeCurrency}
                    value={selectedIsoCurrencyCode}
                >
                    <option key="multiple" value="">Multiple</option>
                    {configuration.homeTargetCurrencies.map((homeTargetCurrency) =>
                        (
                            <option
                                key={homeTargetCurrency.source}
                                value={homeTargetCurrency.source}
                            >
                                {homeTargetCurrency.source}
                            </option>
                        )
                    )}
                </select>
            </div>
        );
    }

    private renderPromotionToggle(): React.ReactNode {
        const { configuration, showPromotionsOnly } = this.props;
        if (!configuration.doesBudgetForAmountCodeExist(AmountCode.Promotion)) {
            return null;
        }

        return (
            <>
                <Toggle
                    className="second-sub-header__promotion-only-toggle"
                    id="promotion-only-toggle"
                    value={showPromotionsOnly}
                    onChange={this.handleChangePromotionToggle}
                />
                <span className="second-sub-header__promotion-only-toggle-text">Promotion Requests Only</span>
            </>
        );
    }

    private searchAllJobTitles(searchText: string): string[] {
        const { teamDecisionStore } = this.props;
        const jobTitleSet: Set<string> = new Set();
        teamDecisionStore.countryBudgetDecisions.forEach(cbd => cbd.jobTitles.reduce((s, e) => s.add(e), jobTitleSet));
        return Array.from(jobTitleSet).filter(jobTitle => jobTitle?.toLowerCase().includes(searchText.toLowerCase()));
    }

    private searchAllDepartments(searchText: string): string[] {
        const { teamDecisionStore } = this.props;
        const departmentsSet: Set<string> = new Set();
        teamDecisionStore.countryBudgetDecisions.forEach(cbd => cbd.departments.reduce((s, e) => s.add(e), departmentsSet));
        return Array.from(departmentsSet).filter(dept => dept?.toLowerCase().includes(searchText.toLowerCase()));
    }

    private setPlaceHolderText(): string {
        const { teamView } = this.props.teamDecisionStore;
        return teamView === ReportsView.DirectsOnly ? 'Search directs only' : 'Search all employees';
    }

    private handleSetFoundEmployee(employee: ISearchedEmployee): void {
        const { teamDecisionStore } =  this.props;
        teamDecisionStore.setSearchedEmployee(employee.employeeSourceId);
    }

    private handleSetFilterByJobTitle(jobTitle: string): void {
        const { teamDecisionStore, navigationStore } =  this.props;
        teamDecisionStore.setSearchedFilter(jobTitle, null);
        navigationStore.navigateToUpdateState({JobTitleFilter: jobTitle} as IEmployeeViewNavigationParameters);
    }

    private handleSetFilterByDepartment(department: string): void {
        const { teamDecisionStore, navigationStore } =  this.props;
        teamDecisionStore.setSearchedFilter(null, department);
        navigationStore.navigateToUpdateState({DepartmentFilter: department} as IEmployeeViewNavigationParameters);
    }

    private clearFoundEmployee(): void {
        const { teamDecisionStore } = this.props;
        teamDecisionStore.clearSearchedEmployee();
    }

    private clearSearchedJobTitle(): void {
        const { teamDecisionStore, navigationStore } = this.props;
        teamDecisionStore.clearSearchedFilter(true);
        navigationStore.navigateToUpdateState({JobTitleFilter: null} as IEmployeeViewNavigationParameters);
    }

    private clearSearchedDepartment(): void {
        const { teamDecisionStore, navigationStore } = this.props;
        teamDecisionStore.clearSearchedFilter(false);
        navigationStore.navigateToUpdateState({DepartmentFilter: null} as IEmployeeViewNavigationParameters);
    }

    private async searchEmployees(searchText: string, resultLimit: number, managerId: string[]):
        Promise<ISearchedEmployee[]> {
        const { teamDecisionStore } =  this.props;
        return await teamDecisionStore.searchEmployees(searchText, resultLimit, managerId);
    }

    private handleGetSelectedValue(employee: ISearchedEmployee): string {
        return employee.displayName;
    }

    private handleGetFilter(filter: string): string {
        return filter;
    }

    private getSearchSuggestionText(employee: ISearchedEmployee): string {
        return `${employee.employeeSourceId} - ${employee.displayName}`;
    }

    private handleChangeCurrency(event: React.ChangeEvent<HTMLSelectElement>): void {
        const { teamDecisionDispatcher } = this.props;
        teamDecisionDispatcher.dispatchIsoCurrencyCodeChanged(event.target.value);
    }

    private handleChangeEmployeeSort(event: React.ChangeEvent<HTMLSelectElement>): void {
        const { teamDecisionDispatcher } = this.props;
        teamDecisionDispatcher.dispatchEmployeeSortOptionChanged(event.target.value as EmployeeSort);
    }

    private handleChangePromotionToggle(_: string, value: boolean): void {
        const { teamDecisionDispatcher } = this.props;
        teamDecisionDispatcher.dispatchPromotionOnlyChanged(value);
    }

    private async handleExportReportsToCsv(): Promise<void> {
        const { teamDecisionStore, currentManagerIds } = this.props;
        await teamDecisionStore.exportReportsToCsv(currentManagerIds);
    }

    private async handleChangeHistoryViewClick(): Promise<void> {
        const {
            navigationStore,
            configuration,
            currentManagerIds,
            teamDecisionStore,
            workflowStatus} = this.props;
        navigationStore.navigateToChangeHistory(
            configuration.cycleId, currentManagerIds[0], workflowStatus, teamDecisionStore.teamView);
    }

    private handleChangeView(view: ReportsView): void {
        const {
            teamDecisionStore,
            currentManagerIds,
            configuration,
            budgetCycleId,
            navigationStore
        } = this.props;
        teamDecisionStore.setTeamView(view);

        // For manager view navigation, exactly one of workflow status or budget cycle id must exist.
        // So to go to an over budget view, we need to make sure to pass in null for workflow status
        // when budget cycle id exists.
        const workflowStatus = budgetCycleId == null ? this.props.workflowStatus : null;
        navigationStore.handleReportsViewNavigation(
            configuration.cycleId,
            currentManagerIds,
            teamDecisionStore.teamView,
            workflowStatus,
            budgetCycleId);
    }

    private handleDirectsClick(): void {
        this.handleChangeView(ReportsView.DirectsOnly);
    }

    private handleAllEmployeesClick(): void {
        this.handleChangeView(ReportsView.AllReports);
    }
}

export default observer(TeamDecisionHeader);